import { Badge, BadgeVariant } from '@/components/ui/badge';
import { cn } from "@/lib/utils";

export type OrderStatus =
  | 'paid'
  | 'open'
  | 'pending'
  | 'failed'
  | 'cancelled'
  | 'refunded'
  | 'partially_refunded'
  | 'to_be_paid';

export interface OrderStatusBadgeProps {
  status: string;
  className?: string;
}

// Get badge variant based on order status
const getOrderStatusBadgeVariant = (status: string): BadgeVariant => {
  const normalizedStatus = status?.toLowerCase() || '';

  // Success states
  if (normalizedStatus === 'paid') return 'green';

  // Pending/open states
  if (['open', 'pending', 'to_be_paid'].includes(normalizedStatus)) return 'blue';

  // Warning states
  if (['refunded', 'partially_refunded'].includes(normalizedStatus)) return 'yellow';

  // Error/failure states
  if (['failed', 'cancelled', 'canceled'].includes(normalizedStatus)) return 'red';

  return 'gray';
};

// Get display label for order status
const getOrderStatusLabel = (status: string): string => {
  const normalizedStatus = status?.toLowerCase() || '';

  const statusMap: Record<string, string> = {
    paid: 'Paid',
    open: 'Open',
    pending: 'Pending',
    failed: 'Failed',
    cancelled: 'Cancelled',
    canceled: 'Canceled',
    refunded: 'Refunded',
    partially_refunded: 'Partially Refunded',
    to_be_paid: 'To Be Paid',
  };

  return statusMap[normalizedStatus] || status?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Unknown';
};

export function OrderStatusBadge({ status, className }: OrderStatusBadgeProps) {
  return (
    <Badge variant={getOrderStatusBadgeVariant(status)} className={cn('min-w-[72px] justify-center', className)}>
      {getOrderStatusLabel(status)}
    </Badge>
  );
}
